'use client';

import { useStep } from '@skin-seoul/utils';
import { useCallback, useState } from 'react';
import { useAuthService } from './useAuthService';
import type { UserType, VerifyOtpResponse } from '../../infrastructure/auth/utils/types';

/**
 * Enum representing OTP authentication states
 *
 * Flow: IDLE → SENT → VERIFYING → SUCCESS | NEED_REGISTRATION | FAILED
 */
export enum OTPAuthStep {
  IDLE = 'IDLE',
  SENT = 'SENT',
  VERIFYING = 'VERIFYING',
  SUCCESS = 'SUCCESS',
  NEED_REGISTRATION = 'NEED_REGISTRATION',
  FAILED = 'FAILED',
}

const OTP_AUTH_STEPS = [
  OTPAuthStep.IDLE,
  OTPAuthStep.SENT,
  OTPAuthStep.VERIFYING,
  OTPAuthStep.SUCCESS,
  OTPAuthStep.NEED_REGISTRATION,
  OTPAuthStep.FAILED,
] as const;

type UseOTPAuthParams = {
  phoneNumber?: string;
  initialStep?: OTPAuthStep;
};

type UseOTPAuthReturn = {
  step: OTPAuthStep;
  otpId: string | undefined;
  otpCode: string | undefined;
  userType: UserType | undefined;
  candidateEmails: string[] | undefined;
  isSending: boolean;
  isVerifying: boolean;
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (code: string) => Promise<boolean>;
  resetStep: () => void;
};

/**
 * Hook for managing OTP authentication flow
 *
 * @description
 * Handles phone number-based OTP generation and verification.
 * Determines the next step based on user type after OTP verification.
 *
 * ## Flow
 * 1. Send OTP (sendOtp) → SENT
 * 2. Verify OTP (verifyOtp) → Branches based on result:
 *    - `guest` → NEED_REGISTRATION (registration required)
 *    - `customer/subscriber` + `otpVerified: false` → SUCCESS + returns candidateEmails
 *    - `customer/subscriber` + `otpVerified: true` → SUCCESS (login complete)
 *
 * ## Email Verification (candidateEmails)
 * - Returns candidateEmails for existing users with unverified OTP accounts.
 * - Email selection verification is handled separately by `useEmailVerification` hook.
 *
 * @example
 * ```tsx
 * const { step, sendOtp, verifyOtp, userType, candidateEmails } = useOTPAuth({
 *   phoneNumber: '+821012345678',
 * });
 *
 * // Send OTP
 * await sendOtp();
 *
 * // Verify OTP
 * const isSuccess = await verifyOtp('123456');
 *
 * // Handle post-verification branching
 * if (step === OTPAuthStep.SUCCESS && candidateEmails) {
 *   // Navigate to email selection → use useEmailVerification
 * } else if (step === OTPAuthStep.NEED_REGISTRATION) {
 *   // Navigate to registration
 * } else if (step === OTPAuthStep.SUCCESS) {
 *   // Login complete
 * }
 * ```
 */
export const useOTPAuth = ({ phoneNumber, initialStep }: UseOTPAuthParams = {}): UseOTPAuthReturn => {
  const [step, { setStep, reset: resetStep }] = useStep({
    steps: OTP_AUTH_STEPS,
    defaultStep: initialStep ?? OTPAuthStep.IDLE,
  });

  const { useGenerateOtp, useVerifyOtp } = useAuthService();
  const { mutateAsync: generateOtpMutation, isPending: isSending } = useGenerateOtp();
  const { mutateAsync: verifyOtpMutation, isPending: isVerifying } = useVerifyOtp();

  const [otpId, setOtpId] = useState<string | undefined>(undefined);
  const [otpCode, setOtpCode] = useState<string | undefined>(undefined);
  const [userType, setUserType] = useState<UserType | undefined>(undefined);
  const [candidateEmails, setCandidateEmails] = useState<string[] | undefined>(undefined);

  const sendOtp = useCallback(async () => {
    if (!phoneNumber) {
      return;
    }

    const res = await generateOtpMutation({ phone: phoneNumber });
    setOtpId(res.data?.otpId);
    setStep(OTPAuthStep.SENT);
  }, [phoneNumber, generateOtpMutation, setStep]);

  const resendOtp = useCallback(async () => {
    await sendOtp();
  }, [sendOtp]);

  const handleVerifyOtpResponse = useCallback(
    (response: VerifyOtpResponse, code: string): boolean => {
      // TODO: response.otpVerificationInfo need to check
      if (!response.success || !response.otpVerificationInfo) {
        setStep(OTPAuthStep.FAILED);
        return false;
      }

      const { user } = response.otpVerificationInfo;
      setUserType(user.userType);
      setOtpCode(code);

      if (user.userType === 'guest') {
        setStep(OTPAuthStep.NEED_REGISTRATION);
        return true;
      }

      if (user.otpVerified === false && user.candidateEmails) {
        setCandidateEmails(user.candidateEmails);
        setStep(OTPAuthStep.SUCCESS);
        return true;
      }

      if (user.otpVerified === true) {
        setStep(OTPAuthStep.SUCCESS);
        return true;
      }

      setStep(OTPAuthStep.FAILED);
      return false;
    },
    [setStep],
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<boolean> => {
      if (!phoneNumber) {
        return false;
      }

      setStep(OTPAuthStep.VERIFYING);

      const result = await verifyOtpMutation({ phone: phoneNumber, otp: code });
      return handleVerifyOtpResponse(result, code);
    },
    [phoneNumber, verifyOtpMutation, setStep, handleVerifyOtpResponse],
  );

  return {
    step,
    otpId,
    otpCode,
    userType,
    candidateEmails,
    isSending,
    isVerifying,
    sendOtp,
    resendOtp,
    verifyOtp,
    resetStep,
  };
};
